import { ref, computed, watch } from 'vue';
import { useAppState } from './useAppState';
import { useDocuments } from './useDocuments';
import { explainTaxCode } from '@/utils/taxCode';
import { dedupePayslips, buildMonths, combineMonth, unexplainedDeductions, otherDeductions, niceAxis, payslipCoverage, monthOnMonth, estimateMissingMonths, describeMonths, taxYearOfMonth, nearestMonthWithPayslips, isPayslip, isHmrcDoc, parsePayslipMeta, parseHmrcForm, formatTaxYear, formatDate, properCase, FORM_EXPLAINERS } from '@/utils/payslips';
/**
 * Shared Payslips & Tax state for the mobile and desktop pages: loads payslips and HMRC forms, lays them out
 * by month and works out what the chart, month card and tax docs show. Layout and navigation stay in the pages.
 * The selected month lives in app state so back (and leaving and returning) keeps it (DEV-147).
 */
export function usePayslips(opts) {
    const state = useAppState();
    const { getDocuments } = useDocuments();
    const payslips = ref([]);
    const hmrcForms = ref([]);
    const loaded = ref(false);
    const selectedKey = computed({
        get: () => state.payslipsMonth,
        set: (key) => { state.payslipsMonth = key; },
    });
    // Documents reload several times on this page, so each payslip is only flagged once.
    const warnedPayslipIds = new Set();
    const parsedPayslips = computed(() => {
        const parsed = dedupePayslips(payslips.value.map(parsePayslipMeta));
        for (const p of parsed) {
            const diff = unexplainedDeductions(p);
            // Gross − deductions should equal net; flag the ones that don't for extraction review.
            if (Math.abs(diff) >= 0.01 && !warnedPayslipIds.has(p.original?.id)) {
                warnedPayslipIds.add(p.original?.id);
                console.warn('Payslip does not reconcile (gross − deductions ≠ net)', p.original?.id, diff);
            }
        }
        return parsed;
    });
    const months = computed(() => buildMonths(parsedPayslips.value, new Date()));
    const estimates = computed(() => estimateMissingMonths(months.value));
    const coverage = computed(() => payslipCoverage(parsedPayslips.value, months.value, new Date()));
    // Default to the latest month with a payslip.
    watch(months, list => {
        if (!list.some(m => m.key === selectedKey.value)) {
            selectedKey.value = [...list].reverse().find(m => m.payslips.length)?.key ?? list[list.length - 1]?.key ?? null;
        }
    }, { immediate: true });
    const monthTotals = computed(() => months.value.map(m => combineMonth(m.payslips)));
    const selectedIndex = computed(() => months.value.findIndex(m => m.key === selectedKey.value));
    const selectedMonth = computed(() => months.value[selectedIndex.value] || null);
    const selectedPayslip = computed(() => monthTotals.value[selectedIndex.value] || null);
    const selectedMonthName = computed(() => monthName({ month: 'long', year: 'numeric' }));
    const selectedMonthOnly = computed(() => monthName({ month: 'long' }));
    const selectedTaxYear = computed(() => {
        const end = selectedPayslip.value?.periodEnd;
        return end ? taxYearOfMonth(end.getFullYear(), end.getMonth()) : undefined;
    });
    const selectedTaxCode = computed(() => selectedPayslip.value?.taxCode
        ? explainTaxCode(selectedPayslip.value.taxCode, v => state.formatter.format(v, false, false), selectedTaxYear.value)
        : null);
    const selectedEstimate = computed(() => selectedMonth.value ? estimates.value.byMonth.get(selectedMonth.value.key) || null : null);
    const monthOnMonthText = computed(() => monthOnMonth(months.value, selectedIndex.value, v => state.formatter.format(v)));
    // "£X earned in Apr to Jun" for the selected month's tax year, when the YTD can't be split per month.
    const yearToDateNote = computed(() => {
        const m = selectedMonth.value;
        const total = m ? estimates.value.byTaxYear.get(taxYearOfMonth(m.year, m.month)) : null;
        return total ? `${state.formatter.format(total.amount)} earned in ${describeMonths(total.missing)} (from your year-to-date figures). Add those payslips to see the breakdown.` : '';
    });
    const coverageText = computed(() => {
        const c = coverage.value;
        if (!c)
            return '';
        const year = formatTaxYear(c.taxYearStart);
        return c.held >= c.expected ? `All payslips added for ${year}` : `${c.held} of ${c.expected} payslips added for ${year}`;
    });
    const lastPayslipText = computed(() => {
        const latest = coverage.value?.latest;
        if (!latest?.periodEnd)
            return '';
        const month = latest.periodEnd.toLocaleString('default', { month: 'long', year: 'numeric' });
        return latest.createdAt ? `Last payslip: ${month}, added ${formatDate(new Date(latest.createdAt))}` : `Last payslip: ${month}`;
    });
    function monthNameOf(key) {
        const m = months.value.find(mm => mm.key === key);
        return m ? new Date(m.year, m.month, 1).toLocaleString('default', { month: 'long' }) : '';
    }
    function monthName(options) {
        const m = selectedMonth.value;
        return m ? new Date(m.year, m.month, 1).toLocaleString('default', options) : '';
    }
    // Bar = what the member earned, stacked bottom to top. Employer-paid amounts are never stacked on pay.
    const SERIES = [
        // Short labels so the legend fits on one line.
        { key: 'netPay', label: 'Net', cssVar: '--payslip-net' },
        { key: 'tax', label: 'Tax', cssVar: '--payslip-tax' },
        { key: 'ni', label: 'NI', cssVar: '--payslip-ni' },
        { key: 'salarySacrifice', label: 'Salary sacrifice', cssVar: '--payslip-salsac' },
        { key: 'other', label: 'Other', cssVar: '--payslip-other' },
    ];
    function seriesValue(p, key) {
        return key === 'other' ? otherDeductions(p) : p[key] || 0;
    }
    function stackTotal(p) {
        return SERIES.reduce((sum, s) => sum + seriesValue(p, s.key), 0);
    }
    const legendSeries = computed(() => SERIES.filter(s => monthTotals.value.some(p => p && seriesValue(p, s.key) > 0)));
    const hasMissingMonths = computed(() => months.value.some((m, i) => !monthTotals.value[i] && !estimates.value.byMonth.has(m.key)));
    const hasEstimates = computed(() => estimates.value.byMonth.size > 0);
    // Missing months get a placeholder at the average known height (half height when there's only one to go on).
    const chartScale = computed(() => {
        const known = monthTotals.value.filter((p) => !!p).map(stackTotal);
        const tallest = Math.max(0, ...known, ...[...estimates.value.byMonth.values()].map(e => e.gross));
        const placeholder = known.length > 1 ? known.reduce((a, b) => a + b, 0) / known.length : (tallest || 1000) / 2;
        return { placeholder, ...niceAxis(Math.max(tallest, placeholder)) };
    });
    function formatAxisValue(value) {
        return value >= 1000 && value % 1000 === 0
            ? state.formatter.format(value / 1000, true, false) + 'k'
            : state.formatter.format(value, true, false);
    }
    const chartOption = computed(() => {
        const tickColour = opts.isDark() ? 'rgba(232, 228, 223, 0.7)' : '#64748b';
        const splitColour = opts.isDark() ? 'rgba(232, 228, 223, 0.1)' : '#e2e8f0';
        const rootStyle = getComputedStyle(document.documentElement);
        const colours = SERIES.map(s => rootStyle.getPropertyValue(s.cssVar).trim());
        const totals = monthTotals.value;
        const { max, interval, placeholder } = chartScale.value;
        const opacity = (idx) => idx === selectedIndex.value ? 1 : 0.4;
        // Round the top of whichever segment ends up on top of each bar.
        const topSeries = totals.map(p => p ? SERIES.reduce((top, s, i) => seriesValue(p, s.key) > 0 ? i : top, -1) : -1);
        const series = SERIES.map((s, i) => ({
            type: 'bar',
            stack: 'pay',
            barMaxWidth: 48,
            data: totals.map((p, idx) => p ? {
                value: seriesValue(p, s.key),
                itemStyle: { color: colours[i], opacity: opacity(idx), borderRadius: topSeries[idx] === i ? [8, 8, 0, 0] : 0 },
            } : '-'),
        }));
        const estimateFor = (idx) => estimates.value.byMonth.get(months.value[idx].key);
        // Estimated from YTD: hatched in the net pay colour, distinct from real bars and from "No payslip".
        series.push({
            type: 'bar',
            stack: 'pay',
            barMaxWidth: 48,
            data: totals.map((p, idx) => p || !estimateFor(idx) ? '-' : {
                value: estimateFor(idx).gross,
                itemStyle: {
                    color: colours[0],
                    opacity: 0.45 * opacity(idx),
                    borderRadius: [8, 8, 0, 0],
                    decal: { symbol: 'rect', symbolSize: 1, dashArrayX: [1, 0], dashArrayY: [3, 5], rotation: -Math.PI / 4, color: 'rgba(255, 255, 255, 0.7)' },
                },
            }),
        });
        series.push({
            type: 'bar',
            stack: 'pay',
            barMaxWidth: 48,
            data: totals.map((p, idx) => p || estimateFor(idx) ? '-' : {
                value: placeholder,
                itemStyle: { color: 'transparent', borderColor: tickColour, borderWidth: 1.5, borderType: 'dashed', borderRadius: [8, 8, 0, 0], opacity: opacity(idx) },
                label: { show: true, position: 'insideBottom', formatter: '+', color: tickColour, fontSize: 18 },
            }),
        });
        // A genuine £0 payslip: a thin line at the base labelled £0, distinct from a missing month.
        series.push({
            type: 'bar',
            stack: 'pay',
            barMaxWidth: 48,
            data: totals.map((p, idx) => p && stackTotal(p) === 0 ? {
                value: max / 100,
                itemStyle: { color: tickColour, opacity: opacity(idx) },
                label: { show: true, position: 'top', formatter: state.formatter.format(0, true, false), color: tickColour, fontSize: 11 },
            } : '-'),
        });
        return {
            animationDuration: 750,
            animationEasing: 'quarticInOut',
            // Fixed bottom (room for the month labels) so the plot height matches the fixed £ axis chart.
            grid: { left: 0, right: 0, top: 8, bottom: 28 },
            xAxis: {
                type: 'category',
                data: months.value.map(m => m.label),
                axisTick: { show: false },
                axisLine: { show: false },
                // interval 0: never let ECharts drop labels when the longer "Jan '26" gets tight.
                axisLabel: { color: tickColour, fontSize: 13, fontWeight: 500, interval: 0 },
            },
            yAxis: {
                type: 'value',
                min: 0,
                max,
                interval,
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: { lineStyle: { color: splitColour } },
                axisLabel: { show: false, color: tickColour, formatter: formatAxisValue },
            },
            series,
        };
    });
    // Clicking the chart maps the click x to the nearest month, so empty columns select too.
    function onChartClick(chart, event) {
        if (!chart)
            return;
        if (!chart.containPixel('grid', [event.offsetX, event.offsetY]))
            return;
        const idx = chart.convertFromPixel({ xAxisIndex: 0 }, event.offsetX);
        const month = months.value[idx];
        if (month)
            selectedKey.value = month.key;
    }
    const payslipButtonText = computed(() => {
        const count = selectedMonth.value?.payslips.length || 0;
        if (!count)
            return `Add ${selectedMonthOnly.value} payslip`;
        return count > 1 ? `View ${count} payslips` : `View ${selectedMonthOnly.value} payslip`;
    });
    const selectedMonthByEmployer = computed(() => {
        const groups = new Map();
        for (const p of selectedMonth.value?.payslips || []) {
            const employer = properCase(p.employer);
            groups.set(employer, [...(groups.get(employer) || []), p]);
        }
        return [...groups.entries()].map(([employer, payslips]) => ({ employer, payslips }));
    });
    // A P60 is expected for a finished tax year once it's past 31 May, if we hold that year's March payslip.
    const expectedP60Years = computed(() => {
        const today = new Date();
        return [...new Set(parsedPayslips.value
                .filter(p => p.periodEnd && p.periodEnd.getMonth() === 2 && today >= new Date(p.periodEnd.getFullYear(), 5, 1))
                .map(p => formatTaxYear(p.periodEnd.getFullYear() - 1)))];
    });
    const hmrcByYear = computed(() => {
        const groups = new Map();
        const group = (year) => groups.get(year) || groups.set(year, { forms: [], missing: [] }).get(year);
        for (const form of hmrcForms.value)
            group(form.year).forms.push(form);
        for (const year of expectedP60Years.value) {
            if (!group(year).forms.some(f => f.kind === 'P60'))
                group(year).missing.push('P60');
        }
        return [...groups.entries()]
            .map(([year, g]) => ({ year, ...g }))
            .sort((a, b) => b.year.localeCompare(a.year));
    });
    // All three when there are no forms yet, otherwise just the kinds the member has.
    const formExplainers = computed(() => hmrcForms.value.length
        ? FORM_EXPLAINERS.filter(f => hmrcForms.value.some(form => form.kind === f.kind))
        : FORM_EXPLAINERS);
    function aiContextFor(tab) {
        var _a;
        const parts = [];
        if (tab === 'tax') {
            const allKinds = ['P60', 'P11D', 'P45'];
            const present = new Set(hmrcForms.value.map(f => f.kind));
            const missing = allKinds.filter(k => !present.has(k));
            if (hmrcForms.value.length === 0) {
                parts.push('user has NO tax docs on file (no P60, no P11D, no P45)');
            }
            else {
                const byKind = {};
                for (const f of hmrcForms.value) {
                    (byKind[_a = f.kind] || (byKind[_a] = [])).push(f.year);
                }
                const summary = Object.entries(byKind)
                    .map(([k, years]) => `${k} (${[...new Set(years)].sort().join(', ')})`)
                    .join('; ');
                parts.push(`tax docs on file: ${summary}`);
                if (missing.length > 0) {
                    parts.push(`user does NOT have any ${missing.join(' or ')} on file — do not suggest questions about ${missing.join(' or ')}`);
                }
            }
        }
        else {
            if (selectedPayslip.value) {
                const p = selectedPayslip.value;
                parts.push(`latest payslip — tax code ${p.taxCode || 'unknown'}, gross £${Math.round(p.grossPay)}, tax £${Math.round(p.tax)}, NI £${Math.round(p.ni)}, net £${Math.round(p.netPay)}`);
            }
            else if (payslips.value.length === 0) {
                parts.push('user has not uploaded any payslips yet');
            }
        }
        return parts.join('. ');
    }
    // Most relevant first, from the member's data. Pension prompts wait on DEV-128.
    function aiPromptsFor(tab) {
        if (tab === 'tax') {
            if (!hmrcForms.value.length) {
                return ["What's a P60?", 'Where do I find my P60?', "What's the difference between a P60 and a P45?"];
            }
            const kind = hmrcForms.value.some(f => f.kind === 'P60') ? 'P60' : hmrcForms.value[0].kind;
            return [`What does my ${kind} tell me?`, 'Do I need to do a tax return?'];
        }
        const payChanged = monthOnMonthText.value && !monthOnMonthText.value.startsWith('Same as');
        return [...(payChanged ? ['Why did my pay change?'] : []), selectedTaxCode.value?.question || 'Tell me about my tax code'];
    }
    // `onFirstLoad` runs once the first documents land, e.g. to restore the scroll position.
    function loadPayslips(onFirstLoad) {
        getDocuments(() => {
            const docs = state.documents || [];
            payslips.value = docs.filter(isPayslip).filter((doc) => doc.meta);
            hmrcForms.value = docs.filter(isHmrcDoc).filter((doc) => doc.meta).map((doc) => parseHmrcForm(doc, v => state.formatter.format(v)));
            if (!loaded.value) {
                selectedKey.value = nearestMonthWithPayslips(months.value, selectedKey.value);
                onFirstLoad?.();
            }
            loaded.value = true;
        });
    }
    return {
        payslips,
        hmrcForms,
        loaded,
        selectedKey,
        parsedPayslips,
        months,
        estimates,
        coverage,
        monthTotals,
        selectedIndex,
        selectedMonth,
        selectedPayslip,
        selectedMonthName,
        selectedMonthOnly,
        selectedTaxYear,
        selectedTaxCode,
        selectedEstimate,
        monthOnMonthText,
        yearToDateNote,
        coverageText,
        lastPayslipText,
        monthNameOf,
        legendSeries,
        hasMissingMonths,
        hasEstimates,
        chartOption,
        onChartClick,
        payslipButtonText,
        selectedMonthByEmployer,
        hmrcByYear,
        formExplainers,
        aiContextFor,
        aiPromptsFor,
        loadPayslips,
    };
}
